import { HTMLAttributes, Ref, TdHTMLAttributes, ThHTMLAttributes } from 'react';

function cn(...classes: (string | undefined | false | null)[]): string {
  return classes.filter(Boolean).join(' ');
}

interface TableProps extends HTMLAttributes<HTMLTableElement> {
  /**
   * "default" : sticky thead, border-collapse 기반 (Courses, Users, Logs 등)
   * "card"    : 카드 div 로 감싸진 min-w-full divide-y 기반 (Admins 스타일)
   */
  variant?: 'default' | 'card';
  ref?: Ref<HTMLTableElement>;
}

export function Table({ variant = 'default', className, children, ref, ...props }: TableProps) {
  const table = (
    <table
      ref={ref}
      className={cn(
        variant === 'default' ? 'w-full border-collapse text-left' : 'min-w-full divide-y divide-slate-200',
        className,
      )}
      {...props}>
      {children}
    </table>
  );

  if (variant === 'card') {
    return <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">{table}</div>;
  }

  return table;
}

interface TableHeaderProps extends HTMLAttributes<HTMLTableSectionElement> {
  sticky?: boolean;
  ref?: Ref<HTMLTableSectionElement>;
}

export function TableHeader({ sticky = true, className, children, ref, ...props }: TableHeaderProps) {
  return (
    <thead ref={ref} className={cn('bg-slate-50', sticky && 'sticky top-0 z-10 shadow-sm', className)} {...props}>
      {children}
    </thead>
  );
}

interface TableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {
  ref?: Ref<HTMLTableSectionElement>;
}

export function TableBody({ className, children, ref, ...props }: TableBodyProps) {
  return (
    <tbody ref={ref} className={cn('divide-y divide-slate-100 bg-white', className)} {...props}>
      {children}
    </tbody>
  );
}

interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  /** Logs의 expanded row처럼 활성 배경색이 필요할 때 */
  isExpanded?: boolean;
  ref?: Ref<HTMLTableRowElement>;
}

export function TableRow({ isExpanded, onClick, className, children, ref, ...props }: TableRowProps) {
  return (
    <tr
      ref={ref}
      onClick={onClick}
      className={cn(
        'transition-colors',
        onClick && 'group cursor-pointer',
        isExpanded ? 'bg-indigo-50' : onClick && 'hover:bg-slate-50',
        className,
      )}
      {...props}>
      {children}
    </tr>
  );
}

interface TableHeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
  ref?: Ref<HTMLTableCellElement>;
}

export function TableHead({ className, children, ref, ...props }: TableHeadProps) {
  return (
    <th
      ref={ref}
      className={cn(
        'border-b border-slate-200 px-6 py-3 text-xs font-semibold tracking-wider text-slate-500 uppercase',
        className,
      )}
      {...props}>
      {children}
    </th>
  );
}

interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {
  mono?: boolean;
  nowrap?: boolean;
  ref?: Ref<HTMLTableCellElement>;
}

export function TableCell({ mono, nowrap, className, children, ref, ...props }: TableCellProps) {
  return (
    <td
      ref={ref}
      className={cn('px-6 py-4 text-sm text-slate-600', mono && 'font-mono', nowrap && 'whitespace-nowrap', className)}
      {...props}>
      {children}
    </td>
  );
}
